import React from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Clock, Crown, Star, ChevronRight
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

// Get current step info
const getCurrentStep = (user) => {
  if (!user) return { label: 'Connexion', step: 0 };
  
  // Admin has full access
  if (user.is_admin) {
    return { label: 'Administrateur', step: 5, complete: true };
  }
  
  if (!user.profile_complete) {
    return { label: 'Complétez votre profil', step: 1, action: '/profile' };
  }
  if (!user.is_validated) {
    return { label: 'Validation en cours', step: 2, waiting: true };
  }
  if (!user.is_subscribed) {
    return { label: 'Abonnez-vous', step: 3, action: '/subscription' };
  }
  return { label: 'Membre actif', step: 4, complete: true };
};

export const MemberJourney = () => {
  const { user } = useAuth();
  
  if (!user) return null;
  
  const currentStep = getCurrentStep(user);
  
  return (
    <div className="hidden md:block p-3 border-b border-white/10 bg-gradient-to-b from-dark to-dark-paper" data-testid="member-journey">
      <Link to="/profile" className="block">
        <div className="flex items-center gap-3">
          {/* Profile Picture with gold border */}
          <div className="relative flex-shrink-0">
            <Avatar className="w-16 h-16 border-2 border-gold shadow-lg" style={{ boxShadow: '0 0 12px rgba(212, 175, 55, 0.4)' }}>
              {user.profile_picture ? (
                <AvatarImage 
                  src={getImageUrl(user.profile_picture)} 
                  alt={user.pseudo}
                  className="object-cover"
                />
              ) : (
                <AvatarFallback className="bg-dark-subtle text-gold text-lg">
                  {user.pseudo?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              )}
            </Avatar>
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white font-semibold text-sm truncate">{user.pseudo}</span>
              {user.is_ambassador && <Crown className="w-4 h-4 text-gold" />}
            </div>
            
            {/* Current step */}
            <div className="flex items-center gap-1.5">
              {currentStep.waiting ? (
                <Clock className="w-3.5 h-3.5 text-amber-400" />
              ) : currentStep.complete ? (
                <Star className="w-3.5 h-3.5 text-gold" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-gold" />
              )}
              <span className={`text-xs ${currentStep.complete ? 'text-gold' : currentStep.waiting ? 'text-amber-400' : 'text-gray-400'}`}>
                {currentStep.label}
              </span>
            </div>
          </div>
        </div>
      </Link>
      
      {/* Action button if needed */}
      {currentStep.action && !currentStep.complete && (
        <Link 
          to={currentStep.action}
          className="mt-2 block w-full text-center py-1.5 bg-gold/10 hover:bg-gold/20 text-gold text-xs font-medium rounded transition-colors"
        >
          {currentStep.label}
        </Link>
      )}
    </div>
  );
};

export default MemberJourney;
